import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";
import type { Category } from "./categories";

export interface Item {
  id: number;
  category_id: number;
  category?: Category;
  name: string;
  notes: string;
  quantity: number;
  price_cents: number;
  created_at: Dayjs;
  updated_at: Dayjs;
}

export interface ItemListResponse {
  items: Item[];
  next_cursor: string | null;
  total_estimate: number;
}

export interface ItemListParams {
  q?: string;
  category_id?: number;
  cursor?: string;
  limit?: number;
}

function qs(p: ItemListParams): string {
  const u = new URLSearchParams();
  if (p.q) u.set("q", p.q);
  if (p.category_id) u.set("category_id", String(p.category_id));
  if (p.cursor) u.set("cursor", p.cursor);
  if (p.limit) u.set("limit", String(p.limit));
  const s = u.toString();
  return s ? "?" + s : "";
}

export function useItemList(params: ItemListParams = {}) {
  return useQuery<ItemListResponse>({
    queryKey: ["items", params],
    queryFn: () => apiGet<ItemListResponse>("/items" + qs(params)),
  });
}

export interface ItemInput {
  category_id: number;
  name: string;
  notes?: string;
  quantity: number;
  price_cents: number;
}

export function useCreateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ItemInput) => apiPost<Item>("/items", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items"] }),
  });
}

export function useUpdateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<ItemInput> & { id: number }) =>
      apiPut<Item>(`/items/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items"] }),
  });
}

export function useDeleteItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete<void>(`/items/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items"] }),
  });
}
